import React from 'react';

import { Tooltip } from 'antd';
import {
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_SOIL_INPUT,
  TASKS_GRID_NUTRIENT_RATE_UNIT,
  TASKS_SHEET_MOA_GROUP_PREFIX,
  TASKS_SHEET_PRODUCTION_COSTS_LABEL,
  TASKS_SHEET_TASK_TOTAL_LABEL,
  TASKS_SHEET_TASK_TOTAL_OF_PREFIX,
  TASKS_SHEET_TASK_TOTAL_OF_TOOLTIP,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import type { IInputStep } from 'interfaces/IInputStep';
import { IInputTask, InputTaskType } from 'interfaces/IInputTask';
import { formatCurrency } from 'utils/CurrencyUtils';
import MergeClassNames from 'utils/MergeClassNames';

import { calculateStepCostPerArea, getTaskSteps } from './stepCalculations';
import { ALL_STEP_CATEGORY_KEY } from './stepCategoryFilters';
import {
  buildTaskMoaGroups,
  buildTaskNutrientTally,
  formatNutrientRate,
} from './taskCaption';
import type { SheetColumnVisibility } from './tasksViewPreferences';

interface TallyItem {
  text: string;
  unit?: string;
}

// What the application applies in this category: nutrients for Soil input,
// each with its own unit, and mode-of-action groups for Plant protection,
// e.g. "Group 3". No heading: agronomists read these at a glance.
const getTallyItems = (
  task: IInputTask,
  planInput: IInput,
  gUnits: GUnit[],
  categoryKey: string
): TallyItem[] => {
  if (categoryKey === SUMMARY_ENTITY_SOIL_INPUT) {
    return buildTaskNutrientTally(task, planInput, gUnits).map(
      ({ symbol, rate }) => ({
        text: `${symbol} ${formatNutrientRate(rate)}`,
        unit: TASKS_GRID_NUTRIENT_RATE_UNIT,
      })
    );
  }
  if (categoryKey === SUMMARY_ENTITY_PLANT_PROTECTION) {
    return buildTaskMoaGroups(task).map((group) => ({
      text: `${TASKS_SHEET_MOA_GROUP_PREFIX} ${group}`,
    }));
  }
  return [];
};

interface TaskSheetTotalRowProps {
  task: IInputTask;
  /** The task's products in the category: what the $/ha total adds up. */
  steps: IInputStep[];
  planInput: IInput;
  gUnits: GUnit[];
  categoryKey: string;
  /** The Task cell, when this is the task's first row (Summary). */
  leadingCell?: React.ReactNode;
  columns: SheetColumnVisibility;
  /** How many product columns show, Input name included. */
  productColumnCount: number;
  /** The task's "+ Add input", on the left under its products. */
  addControl?: React.ReactNode;
  /** Keep "+ Add input" in view; otherwise it shows on hover of the task's rows. */
  isAddControlPinned?: boolean;
}

// An application's total in the category sheet (OP-2556), on one line under
// its products, as in the legacy app: the nutrients it applies (Soil input) or
// its mode-of-action groups (Plant protection) under the product names, and
// its $/ha for the category under $/ha. "Application total" labels the row
// only when there's nothing else on it. Live totals has the plan's totals; this is
// per application.
const TaskSheetTotalRow: React.FC<TaskSheetTotalRowProps> = ({
  task,
  steps,
  planInput,
  gUnits,
  categoryKey,
  leadingCell,
  columns,
  productColumnCount,
  addControl,
  isAddControlPinned = false,
}) => {
  const items = getTallyItems(task, planInput, gUnits, categoryKey);
  const sumCostPerArea = (from: IInputStep[]) =>
    from.reduce<number | null>((total, step) => {
      const stepCost = calculateStepCostPerArea(step, task, gUnits);
      return stepCost === null ? total : (total ?? 0) + stepCost;
    }, null);
  const costPerArea = sumCostPerArea(steps);
  // Under a filter, the application's $/ha for all its inputs too, so the
  // category's share of it shows (e.g. soil inputs in crop establishment).
  const allInputsCostPerArea =
    categoryKey === ALL_STEP_CATEGORY_KEY
      ? null
      : sumCostPerArea(getTaskSteps(task));

  return (
    <tr className="bg-rowHover text-sm">
      {leadingCell}
      <td
        colSpan={
          columns.costPerHa ? productColumnCount - 1 : productColumnCount
        }
        className="border border-gray-medium px-2 py-1"
      >
        {/* Nutrients or Groups on the left, under the product names, then
            "+ Add input" like a spreadsheet's next line; "Application total"
            on the right, beside the value it labels. */}
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {items.map(({ text, unit }) => (
            <span
              key={text}
              className="whitespace-nowrap font-semibold text-charcoal"
            >
              {text}
              {unit && (
                <span className="font-normal text-gray-muted"> {unit}</span>
              )}
            </span>
          ))}
          <span
            className={MergeClassNames(
              'transition-opacity',
              !isAddControlPinned &&
                'opacity-0 focus-within:opacity-100 group-hover/task:opacity-100'
            )}
          >
            {addControl}
          </span>
          {items.length === 0 && columns.costPerHa && (
            <span className="ml-auto text-gray-dark">
              {task.taskType === InputTaskType.Production
                ? TASKS_SHEET_PRODUCTION_COSTS_LABEL
                : TASKS_SHEET_TASK_TOTAL_LABEL}
            </span>
          )}
        </span>
      </td>
      {columns.costPerHa && (
        <td className="border border-gray-medium px-2 py-1 text-right font-semibold text-charcoal">
          {costPerArea === null ? ' ' : formatCurrency(costPerArea)}
          {allInputsCostPerArea !== null && (
            <Tooltip title={TASKS_SHEET_TASK_TOTAL_OF_TOOLTIP}>
              <div className="text-xs font-normal text-gray-muted">
                {TASKS_SHEET_TASK_TOTAL_OF_PREFIX}{' '}
                {formatCurrency(allInputsCostPerArea)}
              </div>
            </Tooltip>
          )}
        </td>
      )}
    </tr>
  );
};

export default TaskSheetTotalRow;
